import React, { Fragment, useMemo } from 'react';

import { graphql, type PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { HubErrorBoundary, useHubContext } from '@atlassian/company-hub-common';

import {
	handleChatBubbleVariant,
	handleWidgetButtonVariant,
	handleWidgetPosition,
	handleWidgetRadiusType,
	handleWidgetSpaceType,
} from '../../common/utils';
import { AiWidgetConfigProvider } from '../ai-widget-config-context';
import type { aiWidgetProviderQuery } from './__generated__/aiWidgetProviderQuery.graphql';

/**
 * Provides AI widget config via context when available.
 * On query failure or invalid data, it falls back to rendering children only.
 */
const AiWidgetProviderInner = ({
	queryReference,
	children,
}: {
	children: React.ReactNode;
	queryReference: PreloadedQuery<aiWidgetProviderQuery>;
}) => {
	const { cloudId: siteId } = useHubContext();
	const data = usePreloadedQuery(
		graphql`
			query aiWidgetProviderQuery($helpCenterAri: ID!, $cloudId: ID!) {
				helpCenter(cloudId: $cloudId) {
					helpCenterById(helpCenterAri: $helpCenterAri) @optIn(to: "HelpCenterAggBeta") {
						... on HelpCenter {
							helpCenterBranding @optIn(to: ["HelpCenterBrandingTest"]) {
								colors {
									primary
								}
							}
						}
					}
				}
				csmAi_getSupportWidget(helpCenterAri: $helpCenterAri) @optIn(to: "CsmAiHub") {
					... on CsmAiWidgetConfig {
						id
						isEnabled
						position
						theme {
							colorVibeVariant
							radius
							space
							chatColor
						}
					}
				}
			}
		`,
		queryReference,
	);
	const primaryColor = data.helpCenter?.helpCenterById?.helpCenterBranding?.colors?.primary;
	const widgetConfig = data.csmAi_getSupportWidget;

	const supportWidgetConfig = useMemo(():
		| React.ComponentProps<typeof AiWidgetConfigProvider>['supportWidgetConfig']
		| null => {
		if (!widgetConfig || !widgetConfig.id || !widgetConfig.theme || !primaryColor || !siteId) {
			return null;
		}

		return {
			siteId,
			widgetId: widgetConfig.id,
			widgetTheme: {
				primaryColor,
				radius: handleWidgetRadiusType(widgetConfig.theme.radius),
				space: handleWidgetSpaceType(widgetConfig.theme.space),
				chatBubbleVariant: handleChatBubbleVariant(widgetConfig.theme.chatColor),
				widgetButtonVariant: handleWidgetButtonVariant(widgetConfig.theme.colorVibeVariant),
				attribution: null,
			},
			widgetPosition: handleWidgetPosition(widgetConfig.position),
			isEnabled: Boolean(widgetConfig.isEnabled),
		};
	}, [widgetConfig, primaryColor, siteId]);

	if (!supportWidgetConfig) {
		return <Fragment>{children}</Fragment>;
	}

	return (
		<AiWidgetConfigProvider supportWidgetConfig={supportWidgetConfig}>
			{children}
		</AiWidgetConfigProvider>
	);
};

export const AiWidgetProvider = ({
	queryReference,
	children,
}: {
	children: React.ReactNode;
	queryReference: PreloadedQuery<aiWidgetProviderQuery>;
}): React.JSX.Element => {
	return (
		<HubErrorBoundary
			packageName="@atlassian/csm-ai-search-trigger"
			id="AiWidgetProvider"
			fallback={<Fragment>{children}</Fragment>}
		>
			<AiWidgetProviderInner queryReference={queryReference}>{children}</AiWidgetProviderInner>
		</HubErrorBoundary>
	);
};
